import { gridOf, maxFactions, type FactionDef, type Roster } from '@archon3d/core';
import { useState } from 'react';
import {
  MAX_RADIUS,
  MAX_SIZE,
  MIN_RADIUS,
  MIN_SIZE,
  hexRadiusOf,
  type CellChar,
  type EditorState,
  type EditorStore,
} from './editorStore';

const BRUSHES: { brush: CellChar; label: string }[] = [
  { brush: 'L', label: 'Light' },
  { brush: 'D', label: 'Dark' },
  { brush: '~', label: 'Cycling' },
  { brush: '#', label: 'Void' },
];

const AFFINITIES: FactionDef['affinity'][] = ['light', 'dark', 'neutral'];

interface Props {
  state: EditorState;
  store: EditorStore;
  roster: Roster;
  /** Characters in the order the place tool lists them. */
  characters: string[];
  fixedGeometry?: boolean;
}

/** The board's settings, the drawing tools and the sides, in the editor's sidebar. */
export function EditorTools({ state, store, roster, characters, fixedGeometry = false }: Props) {
  const { def, tool } = state;
  const grid = gridOf({ width: def.width, height: def.height, def });
  const cap = maxFactions(grid);
  const dispatch = store.dispatch.bind(store);

  return (
    <>
      <section className="editor-section">
        <h3>Board</h3>
        <label className="editor-field">
          <span>Name</span>
          <input
            value={def.name}
            onChange={(e) => dispatch({ type: 'rename', name: e.target.value })}
            data-testid="editor-name"
          />
        </label>
        <label className="editor-field">
          <span>Description</span>
          <input
            value={def.description ?? ''}
            placeholder="Optional"
            onChange={(e) => dispatch({ type: 'describe', description: e.target.value })}
          />
        </label>
        {!fixedGeometry && (
          <div className="editor-field">
            <span>Grid</span>
            <div className="choice" role="radiogroup" aria-label="Grid">
              {(['square', 'hex'] as const).map((g) => (
                <button
                  key={g}
                  role="radio"
                  aria-checked={grid === g}
                  className={grid === g ? 'active' : ''}
                  onClick={() => dispatch({ type: 'grid', grid: g })}
                  data-testid={`editor-grid-${g}`}
                >
                  {g === 'square' ? 'Squares' : 'Hexes'}
                </button>
              ))}
            </div>
          </div>
        )}
        <label className="editor-field">
          <span>Starting light cycle</span>
          <select
            aria-label="Starting light cycle"
            value={def.luminosity?.phase ?? 'random'}
            onChange={(e) =>
              dispatch({
                type: 'luminosity',
                value:
                  e.target.value === 'random'
                    ? undefined
                    : { phase: Number(e.target.value), direction: def.luminosity?.direction ?? 1 },
              })
            }
          >
            <option value="random">Fair randomized start</option>
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>
                {n === 0 ? '0 · darkest' : n === 5 ? '5 · brightest' : n}
              </option>
            ))}
          </select>
        </label>
        {def.luminosity && (
          <label className="editor-field">
            Cycle direction
            <select
              aria-label="Cycle direction"
              value={def.luminosity.direction}
              onChange={(e) =>
                dispatch({
                  type: 'luminosity',
                  value: {
                    phase: def.luminosity!.phase,
                    direction: Number(e.target.value) as 1 | -1,
                  },
                })
              }
            >
              <option value="1">Toward light</option>
              <option value="-1">Toward dark</option>
            </select>
          </label>
        )}
        {!fixedGeometry &&
          (grid === 'hex' ? (
            <div className="editor-field">
              <span>Radius</span>
              <SizeInput
                label="Radius"
                value={hexRadiusOf(def)}
                min={MIN_RADIUS}
                max={MAX_RADIUS}
                onChange={(radius) => dispatch({ type: 'radius', radius })}
                testId="editor-radius"
              />
              <span className="hint">
                rings around the centre, {MIN_RADIUS}–{MAX_RADIUS}
              </span>
            </div>
          ) : (
            <div className="editor-field">
              <span>Size</span>
              <SizeInput
                label="Width"
                value={def.width}
                onChange={(width) => dispatch({ type: 'resize', width, height: def.height })}
              />
              <span className="muted">×</span>
              <SizeInput
                label="Height"
                value={def.height}
                onChange={(height) => dispatch({ type: 'resize', width: def.width, height })}
              />
            </div>
          ))}
      </section>

      <section className="editor-section">
        <h3>Tools</h3>
        <div className="editor-field">
          <span>Paint</span>
          <div className="choice" role="radiogroup" aria-label="Paint">
            {BRUSHES.map((b) => (
              <button
                key={b.brush}
                role="radio"
                aria-checked={tool === 'paint' && state.brush === b.brush}
                className={tool === 'paint' && state.brush === b.brush ? 'active' : ''}
                onClick={() => dispatch({ type: 'brush', brush: b.brush })}
                data-testid={`brush-${b.brush === '~' ? 'cycling' : b.brush === '#' ? 'void' : b.brush}`}
              >
                <span
                  className={`swatch cell-${b.brush === '~' ? 'cycling' : b.brush === '#' ? 'void' : b.brush}`}
                />
                {b.label}
              </button>
            ))}
          </div>
        </div>
        <div className="editor-field">
          <span>Mark</span>
          <div className="choice" role="radiogroup" aria-label="Mark">
            <button
              role="radio"
              aria-checked={tool === 'power'}
              className={tool === 'power' ? 'active' : ''}
              onClick={() => dispatch({ type: 'tool', tool: 'power' })}
              data-testid="tool-power"
            >
              Power point
            </button>
            <button
              role="radio"
              aria-checked={tool === 'wall'}
              className={tool === 'wall' ? 'active' : ''}
              onClick={() => dispatch({ type: 'tool', tool: 'wall' })}
              data-testid="tool-wall"
            >
              Wall
            </button>
          </div>
        </div>
        <div className="editor-field">
          <span>Place</span>
          <div className="choice" role="radiogroup" aria-label="Side">
            {def.factions.map((f, i) => (
              <button
                key={f.id}
                role="radio"
                aria-checked={tool === 'place' && state.faction === i}
                className={tool === 'place' && state.faction === i ? 'active' : ''}
                title={f.name}
                onClick={() => dispatch({ type: 'piece', faction: i })}
                data-testid={`place-side-${i + 1}`}
              >
                <span className="swatch" style={{ background: f.color }} />
                {i + 1}
              </button>
            ))}
          </div>
          <select
            value={state.character}
            aria-label="Character"
            onChange={(e) => dispatch({ type: 'piece', character: e.target.value })}
            data-testid="place-character"
          >
            {characters.map((id) => (
              <option key={id} value={id}>
                {roster[id]?.name ?? id}
              </option>
            ))}
          </select>
        </div>
        <p className="hint">
          {tool === 'paint' && 'Click a cell to paint it.'}
          {tool === 'power' && 'Click a cell to add or remove a power point.'}
          {tool === 'wall' &&
            (state.wallFrom
              ? 'Now click a neighbouring cell to raise or remove the wall between them.'
              : 'Click a cell, then a neighbour, to raise or remove a wall between them.')}
          {tool === 'place' &&
            'Click a cell to put the piece there; click again with the same piece to clear it.'}
        </p>
      </section>

      <section className="editor-section">
        <h3>Sides</h3>
        {def.factions.map((f, i) => (
          <div className="editor-side" key={f.id}>
            <span className="muted">{i + 1}.</span>
            <input
              type="color"
              value={f.color}
              aria-label={`${f.name} colour`}
              onChange={(e) =>
                dispatch({ type: 'faction', index: i, patch: { color: e.target.value } })
              }
            />
            <input
              value={f.name}
              aria-label={`Side ${i + 1} name`}
              onChange={(e) =>
                dispatch({ type: 'faction', index: i, patch: { name: e.target.value } })
              }
            />
            <select
              value={f.affinity}
              aria-label={`${f.name} affinity`}
              onChange={(e) =>
                dispatch({
                  type: 'faction',
                  index: i,
                  patch: { affinity: e.target.value as FactionDef['affinity'] },
                })
              }
            >
              {AFFINITIES.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
        ))}
        <div className="editor-row">
          <button
            onClick={() => dispatch({ type: 'add-faction' })}
            disabled={def.factions.length >= cap}
            data-testid="add-side"
          >
            Add side
          </button>
          <button
            onClick={() => dispatch({ type: 'remove-faction' })}
            disabled={def.factions.length <= 2}
            data-testid="remove-side"
          >
            Remove last side
          </button>
          <span className="hint">
            {def.factions.length} of {cap} on a {grid === 'hex' ? 'hex' : 'square'} board
          </span>
        </div>
      </section>
    </>
  );
}

/** A number field that tolerates half-typed values: the board resizes as soon as the number is valid. */
function SizeInput({
  label,
  value,
  min = MIN_SIZE,
  max = MAX_SIZE,
  onChange,
  testId,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
  testId?: string;
}) {
  const [text, setText] = useState(String(value));
  const [shown, setShown] = useState(value);
  if (shown !== value) {
    setShown(value);
    setText(String(value));
  }
  return (
    <input
      type="number"
      className="size"
      min={min}
      max={max}
      value={text}
      aria-label={label}
      data-testid={testId}
      onChange={(e) => {
        setText(e.target.value);
        const n = Number(e.target.value);
        if (Number.isInteger(n) && n >= min && n <= max) onChange(n);
      }}
      onBlur={() => setText(String(value))}
    />
  );
}
